import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { Box, Typography } from "@mui/material";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import {
    briefAttachments,
    briefAttachment,
    publicBriefAttachments,
    publicBriefAttachment,
} from "../../api/endpoints";
import { BriefAttachment } from "./constants";
import { IconPlus } from "../../utils/icons";
import { extensionOf, isPreviewableImage, kindLabelOf } from "./attachmentKinds";
import { BriefAttachmentPreview, PreviewTarget } from "./BriefAttachmentPreview";
import { AttachmentRow } from "./AttachmentRow";
import { downloadAttachment } from "./downloadAttachment";
import { downloadPdfBlob } from "../../utils/pdf/tdcPdfTheme";

const ACCEPT = ".doc,.docx,.pdf,.txt,.jpg,.jpeg,.png";
const ALLOWED_EXTENSIONS = ACCEPT.split(",");
const TYPES_HINT = ".doc, .docx, .pdf, .txt, .jpg, .png";
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_COUNT = 10;

export interface BriefAttachmentsHandle {
    // Send queued adds/removes to the brief with this id. Called by the host
    // right after it has persisted the brief, so a brand-new brief works too.
    upload: (briefId: number) => Promise<void>;
}

interface Props {
    brandId?: number;
    projectId?: number | string;
    publicToken?: string;
    publicBase?: string;
    value: BriefAttachment[];
    onChange: (next: BriefAttachment[]) => void;
    onQueueChange?: (hasQueued: boolean) => void;
    disabled?: boolean;
}

export const BriefAttachments = forwardRef<BriefAttachmentsHandle, Props>(
    (
        {
            brandId,
            projectId,
            publicToken,
            publicBase,
            value,
            onChange,
            onQueueChange,
            disabled,
        },
        ref,
    ) => {
        const inputRef = useRef<HTMLInputElement | null>(null);
        const [busy, setBusy] = useState(false);
        const [dragging, setDragging] = useState(false);
        const [pending, setPending] = useState<File[]>([]);
        const [removedIds, setRemovedIds] = useState<number[]>([]);
        const [preview, setPreview] = useState<PreviewTarget | null>(null);
        const [previewUrls, setPreviewUrls] = useState<string[]>([]);

        const kept = value.filter((a) => !removedIds.includes(a.id));
        const total = kept.length + pending.length;
        const canAdd = !disabled && !busy && total < MAX_COUNT;

        useEffect(() => {
            onQueueChange?.(pending.length > 0 || removedIds.length > 0);
        }, [pending, removedIds, onQueueChange]);

        useEffect(() => {
            const urls = pending.map((file) => URL.createObjectURL(file));
            setPreviewUrls(urls);
            return () => urls.forEach((url) => URL.revokeObjectURL(url));
        }, [pending]);

        const uploadUrlFor = (id: number) =>
            publicToken
                ? publicBriefAttachments(publicToken, projectId!, id, publicBase)
                : briefAttachments(brandId!, Number(projectId), id);

        const removeUrlFor = (id: number, fileId: number) =>
            publicToken
                ? publicBriefAttachment(publicToken, projectId!, id, fileId, publicBase)
                : briefAttachment(brandId!, Number(projectId), id, fileId);

        const removeOne = (id: number, fileId: number) =>
            axiosInstant
                .delete(removeUrlFor(id, fileId))
                .then(({ data }) => data?.attachments as BriefAttachment[] | undefined)
                .catch(() => {
                    toast.error("Couldn't remove an attachment. Please try again.");
                    return undefined;
                });

        const uploadOne = (id: number, file: File) => {
            const form = new FormData();
            form.append("file", file);
            return axiosInstant
                .post(uploadUrlFor(id), form)
                .then(({ data }) => data?.attachments as BriefAttachment[] | undefined)
                .catch((e: any) => {
                    const messages: Record<string, string> = {
                        BriefAttachmentTypeNotAllowed: `${file.name}: only ${TYPES_HINT} files are allowed.`,
                        BriefAttachmentTooLarge: `${file.name} is over the 10 MB limit.`,
                        BriefAttachmentLimitReached: `A brief can hold at most ${MAX_COUNT} attachments.`,
                    };
                    toast.error(
                        messages[e?.response?.data?.error] ||
                            `${file.name}: couldn't upload. Please try again.`,
                    );
                    return undefined;
                });
        };

        // Sequential so the server-side count limit is honoured; the queue is
        // cleared up front so a second call (double click, host retry) can't
        // re-send the same files.
        const upload = async (id: number) => {
            const removes = removedIds;
            const adds = pending;
            if (!id || (removes.length === 0 && adds.length === 0)) return;
            setRemovedIds([]);
            setPending([]);
            setBusy(true);

            const steps: Array<() => Promise<BriefAttachment[] | undefined>> = [
                ...removes.map((fileId) => () => removeOne(id, fileId)),
                ...adds.map((file) => () => uploadOne(id, file)),
            ];
            const latest = await steps.reduce<Promise<BriefAttachment[] | undefined>>(
                (chain, step) => chain.then((prev) => step().then((next) => next || prev)),
                Promise.resolve(undefined),
            );

            if (latest) onChange(latest);
            setBusy(false);
        };

        const uploadRef = useRef(upload);
        uploadRef.current = upload;
        useImperativeHandle(ref, () => ({ upload: (id) => uploadRef.current(id) }));

        const queueFiles = (files: FileList | null) => {
            const chosen = Array.from(files || []);
            if (inputRef.current) inputRef.current.value = "";

            let room = MAX_COUNT - total;
            const accepted: File[] = [];
            chosen.forEach((file) => {
                if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
                    toast.error(`${file.name}: only ${TYPES_HINT} files are allowed.`);
                } else if (file.size > MAX_BYTES) {
                    toast.error(`${file.name} is over the 10 MB limit.`);
                } else if (room <= 0) {
                    toast.error(`A brief can hold at most ${MAX_COUNT} attachments.`);
                } else {
                    accepted.push(file);
                    room -= 1;
                }
            });
            if (accepted.length > 0) setPending((prev) => [...prev, ...accepted]);
        };

        return (
            <Box className="flex flex-col gap-2">
                <Box className="flex items-baseline justify-between gap-3">
                    <Typography variant="body1" className="text-text-1">
                        Attachments
                    </Typography>
                    {total > 0 && (
                        <span className="text-[11.5px] text-muted">
                            {total} of {MAX_COUNT}
                        </span>
                    )}
                </Box>

                {total > 0 && (
                    <Box className="flex flex-col gap-2">
                        {kept.map((attachment) => (
                            <AttachmentRow
                                key={attachment.id}
                                name={attachment.name}
                                thumbnail={
                                    isPreviewableImage(attachment.name)
                                        ? attachment.location || undefined
                                        : undefined
                                }
                                subtitle={kindLabelOf(attachment.name)}
                                onPreview={() => setPreview(attachment)}
                                onDownload={
                                    attachment.location
                                        ? () =>
                                              downloadAttachment(
                                                  attachment.location as string,
                                                  attachment.name,
                                              )
                                        : undefined
                                }
                                onRemove={
                                    disabled
                                        ? undefined
                                        : () => setRemovedIds((prev) => [...prev, attachment.id])
                                }
                                busy={busy}
                            />
                        ))}
                        {pending.map((file, index) => (
                            <AttachmentRow
                                key={`${file.name}-${index}`}
                                name={file.name}
                                thumbnail={
                                    isPreviewableImage(file.name) ? previewUrls[index] : undefined
                                }
                                subtitle={`${kindLabelOf(file.name)} · Uploads when you save`}
                                onPreview={() =>
                                    setPreview({ name: file.name, url: previewUrls[index] })
                                }
                                onDownload={() => downloadPdfBlob(file, file.name)}
                                onRemove={
                                    disabled
                                        ? undefined
                                        : () =>
                                              setPending((prev) =>
                                                  prev.filter((_, i) => i !== index),
                                              )
                                }
                                busy={busy}
                                muted
                            />
                        ))}
                    </Box>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={ACCEPT}
                    hidden
                    onChange={(e) => queueFiles(e.target.files)}
                />

                <button
                    type="button"
                    disabled={!canAdd}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        if (canAdd) setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragging(false);
                        if (canAdd) queueFiles(e.dataTransfer.files);
                    }}
                    className={`w-full flex flex-col items-center justify-center gap-1 px-4 py-5 rounded-xl border border-dashed transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                        dragging
                            ? "border-[var(--accent)] bg-tint"
                            : "border-[#EAD9B8] bg-surface hover:bg-tint"
                    }`}
                >
                    <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-accent-text">
                        <IconPlus width={13} height={13} stroke="var(--accent-text)" />
                        {busy ? "Saving…" : "Add attachment"}
                    </span>
                    <span className="text-[11.5px] text-muted text-center">
                        {total >= MAX_COUNT
                            ? `You've reached the ${MAX_COUNT} attachment limit.`
                            : `Drop files here or browse. ${TYPES_HINT}, up to 10 MB each.`}
                    </span>
                </button>

                <BriefAttachmentPreview attachment={preview} onClose={() => setPreview(null)} />
            </Box>
        );
    },
);
